import { cn } from '../../lib/utils'

type BrandLogoVariant = 'inline' | 'hero'

interface BrandLogoProps {
  variant?: BrandLogoVariant
  className?: string
}

const VARIANT_CLASSES: Record<BrandLogoVariant, string> = {
  // Navbar / footer / body — sets text color token and body size
  inline: 'text-[17px] font-bold tracking-tight text-[var(--bc-text-primary)]',
  // Large headline — inherits parent color (designed to live inside bc-display-gradient)
  hero: 'font-extralight tracking-tighter',
}

/**
 * BrandLogo — reusable lockup for "better/close" with purple accent slash.
 *
 * Two variants:
 *   - inline : navbar/footer/body copy size (17px, font-bold, sets text color token)
 *   - hero   : large headline size (extralight, tracking-tighter, inherits text color
 *              from parent — designed to live inside .bc-display-gradient spans)
 *
 * The purple accent on the slash is the canonical BetterClose brand mark —
 * matches Login.tsx hero H2 and WebsiteNavbar.
 *
 * Accessibility: the whole lockup is exposed to screen readers via
 * `aria-label="better/close"`, and the decorative slash is hidden with
 * `aria-hidden`. This prevents the slash from being read as
 * "better slash close" when the component is used as the sole content of a
 * <Link>, which is its most common site-wide usage.
 */
export default function BrandLogo({ variant = 'inline', className }: BrandLogoProps) {
  return (
    <span
      aria-label="better/close"
      className={cn(VARIANT_CLASSES[variant], className)}
    >
      better
      <span aria-hidden="true" className="text-[var(--bc-accent-400)]">
        /
      </span>
      close
    </span>
  )
}
